import type { ServiceLinks } from '@mimir/common/dto/ServiceDocumentDtos';
import { getPath } from '@mimir/common/util/getPath';
import type { ConfigType } from '@mimir/common/dto/keldaConfigType';
import type { MosMessageQueue } from '@mimir/common/dto/mosDtos';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { SourceListKey } from '@mimir/common/dto/liveSourceDtos';
import type { VttId } from '@mimir/common/dto/vttDtos';

// All tenant configs
// https://mimir.mjoll.no/config/api/v1/configs/
export const ofConfigs = (sl: ServiceLinks) => sl.config;

// All tenant configs of a given type
// https://mimir.mjoll.no/config/api/v1/configs/{type}/
export const ofConfigType = (sl: ServiceLinks, type: ConfigType) => getPath(sl.configs, { type });

// Single config
// https://mimir.mjoll.no/config/api/v1/configs/{type}/{configId}
export const ofConfig = (sl: ServiceLinks, type: ConfigType, configId: string) => getPath(sl.config, { type, configId });

// All statuses of a given type
// https://mimir.mjoll.no/config/api/v1/configs/{type}/status
export const ofConfigsStatus = (sl: ServiceLinks, type: ConfigType) => getPath(sl.configsStatus, { type });

// All statuses of a given configuration
// https://mimir.mjoll.no/config/api/v1/configs/{type}/{configId}/status
export const ofConfigStatus = (sl: ServiceLinks, type: ConfigType, configId: string) => getPath(sl.configStatus, { type, configId });

export const ofItemTaskCancel = (sl: ServiceLinks, itemId: string) => getPath(sl.cancelTask, { itemId });

export const ofItems = (sl: ServiceLinks) => sl.items;
export const ofItemMetadata = (sl: ServiceLinks, itemId: string) => getPath(sl.itemMetadata, { itemId });
export const ofBatchItems = (sl: ServiceLinks) => sl.batchItems;
export const ofItemsByExternalId = (sl: ServiceLinks) => sl.itemsByExternalId;
export const ofVirtualClips = (sl: ServiceLinks) => sl.items;
export const ofItem = (sl: ServiceLinks, itemId: string) => getPath(sl.item, { itemId });
export const ofItemCustomIcon = (sl: ServiceLinks, itemId: string) => getPath(sl.itemCustomIcons, { itemId });
export const ofItemUndelete = (sl: ServiceLinks, itemId: string) => getPath(sl.itemUndelete, { itemId });
export const ofItemHighresDeletionSchedule = (sl: ServiceLinks, itemId: string) => getPath(sl.deleteHighres, { itemId });
export const ofThumbnailStrip = (sl: ServiceLinks, itemId: string) => getPath(sl.thumbnailStrip, { itemId });
export const ofThumbnailUrlInTime = (sl: ServiceLinks, itemId: string, inPoint: string) => getPath(sl.thumbnailUrlInTime, { itemId, inPoint });
export const ofItemMediaUsage = (sl: ServiceLinks, itemId: string,) => getPath(sl.itemMediaUsage, { itemId });
export const ofClipList = (sl: ServiceLinks, itemId: string,) => getPath(sl.clipList, { itemId });
export const ofTimeline = (sl: ServiceLinks, itemId: string,) => getPath(sl.timeline, { itemId });
export const ofItemComments = (sl: ServiceLinks, itemId: string) => getPath(sl.itemComments, { itemId });
export const ofItemComment = (sl: ServiceLinks, itemId: string, commentId: string) => getPath(sl.itemComment, { itemId, commentId });
export const ofItemCommentStatus = (sl: ServiceLinks, itemId: string, commentId: string) => getPath(sl.itemCommentStatus, { itemId, commentId });
export const ofItemCommentEdit = (sl: ServiceLinks, itemId: string, commentId: string) => getPath(sl.itemCommentEdit, { itemId, commentId });
export const ofItemCommentReactions = (sl: ServiceLinks, itemId: string, commentId: string) => getPath(sl.itemCommentReactions, { itemId, commentId });
export const ofItemArchive = (sl: ServiceLinks, itemId: string) => getPath(sl.itemArchive, { itemId });
export const ofArchiveTransfer = (sl: ServiceLinks, itemId: string) => getPath(sl.archiveTransfer, { itemId });
export const ofItemWarnings = (sl: ServiceLinks, itemId: string) => getPath(sl.warnings, { itemId });

export const ofItemAnalysis = (sl: ServiceLinks, itemId: string) => getPath(sl.itemAnalysis, { itemId });
export const ofItemAncestry = (sl: ServiceLinks, itemId: string) => getPath(sl.itemAncestry, { itemId });

export const ofGeneratedMediaUploadUrl = (sl: ServiceLinks, itemId: string) =>
  getPath(sl.generatedMediaUpload, { itemId });

export const ofPersons = (sl: ServiceLinks) => sl.persons;
export const ofPerson = (sl: ServiceLinks, personId: string) => getPath(sl.person, { personId });

export const ofImages = (sl: ServiceLinks, personId: string) => getPath(sl.personImages, { personId });
export const ofImage = (sl: ServiceLinks, personId: string, imageId: string) => getPath(sl.personImage, { personId, imageId });

export const ofProcess = (sl: ServiceLinks) => sl.process;
export const ofSearch = (sl: ServiceLinks) => sl.search;

export const ofMediaSegments = (sl: ServiceLinks, itemId: string, streamIds: string, signedForMethod: string, firstSegmentIndex: string) => getPath(sl.mediaSegments, { itemId, streamIds, signedForMethod, firstSegmentIndex });
export const ofMetering = (sl: ServiceLinks) => sl.metering;

export const ofStats = (sl: ServiceLinks) => sl.stats;

export const ofStorageManagers = (sl: ServiceLinks) => sl.storageManagers;

export const ofIngestStorageConfigs = (sl: ServiceLinks) => sl.ingestStorageConfigs;
export const ofIngestStorageConfig = (sl: ServiceLinks, itemId: string) => getPath(sl.ingestStorageConfig, { itemId });
export const ofVerifyExternalStorage = (sl: ServiceLinks) => sl.verifyExternalStorage;
export const ofCatchUpStorageIngest = (sl: ServiceLinks) => sl.catchUpStorageIngest;
export const ofGeneratedMediaLocations = (sl: ServiceLinks) => sl.generatedMediaLocations;
export const ofGeneratedMediaLocation = (sl: ServiceLinks, id: string) => getPath(sl.generatedMediaLocation, { id });
export const ofIngestFile = (sl: ServiceLinks) => sl.ingestFile;

export const ofTransferRequests = (sl: ServiceLinks, storageId: string) => getPath(sl.transferRequests, { storageId });
export const ofTransferRequest = (
  sl: ServiceLinks,
  storageId: string,
  transferRequestId: string,
) => getPath(sl.transferRequest, { storageId, transferRequestId });
export const ofMarkTransferRequest = (
  sl: ServiceLinks,
  storageId: string,
  transferRequestId: string,
) => getPath(sl.markTransfer, { storageId, transferRequestId });

export const ofKeldaAdmin = (sl: ServiceLinks) => sl.keldaAdmin;

export const ofKeldaPin = (sl: ServiceLinks, keldaId: string) => getPath(sl.keldaPin, {keldaId});

export const ofKeldaPinTemplate = (sl: ServiceLinks, keldaId: string) => getPath(sl.keldaPinTemplate, {keldaId});

export const ofTenants = (sl: ServiceLinks) => sl.tenants;
export const ofTenantIds = (sl: ServiceLinks) => sl.tenantIds;

export const ofTenant = (sl: ServiceLinks, tenantId: string) => getPath(sl.tenant, { tenantId });
export const ofTenantSettings = (sl: ServiceLinks, tenantId: string) => getPath(sl.tenantSettings, { tenantId });

export const ofTenantStorages = (sl: ServiceLinks) => sl.tenantStorages;
export const ofTenantStorage = (sl: ServiceLinks, storageId: string) => getPath(sl.tenantStorage, { storageId });

export const ofFolders = (sl: ServiceLinks) => sl.folders;
export const ofFolder = (sl: ServiceLinks, folderId: string) => getPath(sl.folder, { folderId });

export const ofFolderContent = (sl: ServiceLinks, folderId: string) => getPath(sl.folderContent, { folderId });
export const ofFolderAncestry = (sl: ServiceLinks) => sl.ancestry;
export const ofCreateFolderPath = (sl: ServiceLinks) => sl.folderPath;
export const ofUpdateFolderMetadata = (sl: ServiceLinks, folderId: string) => getPath(sl.folderMetadata, { folderId });
export const ofFolderSafe = (sl: ServiceLinks, folderId: string) => getPath(sl.folderSafe, { folderId });

export const ofSystemConfigTranscriptionLanguageSettings = (sl: ServiceLinks) => sl.systemConfigTranscriptionLanguageSettings;
export const ofTenantConfigTranscriptionLanguageSettings = (sl: ServiceLinks) => sl.tenantConfigTranscriptionLanguageSettings;

export const ofTenantConfigDisplayAspectRatio = (sl: ServiceLinks) => sl.tenantConfigDisplayAspectRatios;

export const ofTenantConfigDefaultExportLocation = (sl: ServiceLinks) => sl.tenantConfigDefaultExportLocation;
export const ofTenantConfigDefaultUploadLocation = (sl: ServiceLinks) => sl.tenantConfigDefaultUploadLocation;
export const ofTenantConfigDefaultArchiveLocation = (sl: ServiceLinks) => sl.tenantConfigDefaultArchiveLocation;
export const ofTenantConfigUploadLocations = (sl: ServiceLinks) => sl.tenantConfigUploadLocations;
export const ofTenantConfigArchiveLocations = (sl: ServiceLinks) => sl.tenantConfigArchiveLocations;
export const ofTenantConfigColdStorage = (sl: ServiceLinks) => sl.tenantConfigColdStorage;
export const ofColdStorageConfigStatus = (sl: ServiceLinks) => sl.coldStorageConfigStatus;
export const ofLifecycleConfig = (sl: ServiceLinks) => sl.lifecycleConfig;

export const ofTimedMetadata = (sl: ServiceLinks, itemId: string) => getPath(sl.timedMetadata, { itemId });
export const ofTimedMetadataById = (sl: ServiceLinks, itemId: string, timedMetadataId: string) => getPath(sl.timedMetadataById, { itemId, timedMetadataId });
export const ofTranscript = (sl: ServiceLinks, itemId: string) => getPath(sl.transcript, { itemId });

export const ofSettings = (sl: ServiceLinks) => sl.tenantConfig;

export const ofMdfs = (sl: ServiceLinks) => sl.mdfs;
export const ofMdf = (sl: ServiceLinks, mdfId: string) => getPath(sl.mdf, { mdfId });
export const ofOptionLists = (sl: ServiceLinks) => sl.optionLists;
export const ofOptionList = (sl: ServiceLinks, optionListId: string) => getPath(sl.optionList, { optionListId });

export const ofPlayoutDevices = (sl: ServiceLinks) => sl.playoutDevices;
export const ofPlayoutStatus = (sl: ServiceLinks) => sl.playoutStatuses;
export const ofPlayoutCommands = (sl: ServiceLinks) => sl.playoutCommands;

export const ofLabels = (sl: ServiceLinks, itemId: string) => getPath(sl.labels, { itemId });

export const ofUsers = (sl: ServiceLinks) => sl.users;
export const ofUser = (sl: ServiceLinks, userId: string) => getPath(sl.user, { userId });
export const ofUserSignOut = (sl: ServiceLinks, userId: string) => getPath(sl.userSignOut, { userId });

export const ofGroups = (sl: ServiceLinks) => sl.groups;
export const ofGroup = (sl: ServiceLinks, groupId: string) => getPath(sl.group, { groupId });
export const ofTenantAdmins = (sl: ServiceLinks) => sl.tenantAdmins;

export const ofItemAnalytics = (sl: ServiceLinks, itemId: string) => getPath(sl.itemAnalytics, { itemId });
export const ofSearchItemAnalytics = (sl: ServiceLinks) => sl.searchItemAnalytics;
export const ofTenantSystemActivity = (sl: ServiceLinks) => sl.tenantSystemActivity;
export const ofItemActivity = (sl: ServiceLinks, itemId: string) => getPath(sl.itemSystemActivity, { itemId });

export const ofItemUploadPartsSignedUrls = (sl: { itemUploadPartsSignUrls: string }, itemId: string) => getPath(sl.itemUploadPartsSignUrls, { itemId });
export const ofItemMultipartUploadComplete = (sl: { itemMultipartUploadComplete: string }, itemId: string) => getPath(sl.itemMultipartUploadComplete, { itemId });

export const ofRestoreMedia = (sl: ServiceLinks, itemId: string) => getPath(sl.restoreMedia, { itemId });
export const ofTransfers = (sl: ServiceLinks) => sl.transfers;

export const ofUserSettings = (sl: ServiceLinks) => sl.userSettings;
export const ofUserInfo = (sl: ServiceLinks) => sl.userInfo;
export const ofAvatar = (sl: ServiceLinks) => sl.avatar;
export const ofUserStatus = (sl: ServiceLinks) => sl.userStatus;

export const ofVersionItem = (sl: ServiceLinks, itemId: string) => getPath(sl.version, { itemId });

export const ofVersionsHistory = (sl: ServiceLinks, itemId: string) => getPath(sl.versionsHistory, { itemId });

export const ofRelations = (sl: ServiceLinks, itemId: string) => getPath(sl.relations, { itemId });
export const ofRelationType = (sl: ServiceLinks, itemId: string, relationType: string) => getPath(sl.relationType, { itemId, relationType });
export const ofRelation = (sl: ServiceLinks, itemId: string, relationType: string, relatedItemId: string) => getPath(sl.relation, { itemId, relationType, relatedItemId });

export const ofAttachments = (sl: Pick<ServiceLinks, 'attachments'>, itemId: string) => getPath(sl.attachments, { itemId });

export const ofKeldaRequests = (sl: ServiceLinks) => sl.processorRequests;
export const ofPagedKeldaRequests = (sl: ServiceLinks) => sl.keldaRequests;
export const ofKeldaRequest = (sl: ServiceLinks, processorRequestId: string) => getPath(sl.processorRequest, { processorRequestId });
export const ofKeldaRequestClaim = (sl: ServiceLinks) => sl.claimProcessorRequests;
export const ofKeldaWorkerConfig = (sl: ServiceLinks, id: string) => getPath(sl.keldaWorkerConfig, { id });
export const ofKeldaSupervisorConfig = (sl: ServiceLinks, id: string) => getPath(sl.keldaSupervisorConfig, { id });
export const ofPackages = (sl: ServiceLinks, id: string) => getPath(sl.packages, { id });
export const ofRecordLog = (sl: ServiceLinks) => sl.recordLog;
export const ofChangelogs = (sl: ServiceLinks) => sl.changelogs;
export const ofFeatureFlags = (sl: ServiceLinks) => sl.featureFlags;
export const ofFeatureFlag = (sl: ServiceLinks, id: string) => getPath(sl.featureFlag, { id });
export const ofBookmarks = (sl: ServiceLinks) => sl.bookmarks;
export const ofBookmark = (sl: ServiceLinks, id: string) => getPath(sl.bookmark, { id });
export const ofBookmarkFolder = (sl: ServiceLinks) => sl.bookmarkFolders;
export const ofMos = (sl: ServiceLinks, keldaId: string) => getPath(sl.mos, { id: keldaId });
export const ofNextMosMessage = (sl: ServiceLinks, keldaId: string, queue: MosMessageQueue) => getPath(sl.nextMosMessage, { id: keldaId, queue });
export const ofMosAdminMessages = (sl: ServiceLinks, keldaId: string) => getPath(sl.mosAdminMessages, { keldaId });

export const ofAaf = (sl: ServiceLinks, itemId: string) => getPath(sl.aaf, { itemId });

export const ofBulkOperations = (sl: ServiceLinks) => sl.bulkOperations;
export const ofBulkOperation = (sl: ServiceLinks, operationId: string) =>  getPath(sl.bulkOperation, { operationId });

export const ofLiveItems = (sl: ServiceLinks) => sl.liveItems;
export const ofLiveItem = (sl: ServiceLinks, itemId: string) => getPath(sl.liveItem, { id: itemId });
export const ofScheduledRecordings = (sl: ServiceLinks) => sl.scheduledRecordings;
export const ofScheduledRecording = (sl: ServiceLinks, itemId: string) => getPath(sl.scheduledRecording, { id: itemId });
export const ofQc = (sl: ServiceLinks, itemId: string) => getPath(sl.qc, { id: itemId });
export const ofRecordingSeriesCollection = (sl: ServiceLinks) => sl.recordingSeriesCollection;
export const ofRecordingSeries = (sl: ServiceLinks, seriesId: string) => getPath(sl.recordingSeries, { id: seriesId });
export const ofRecordingSeriesItems = (sl: ServiceLinks, seriesId: string) => getPath(sl.recordingSeriesItems, { id: seriesId });

export const ofMergeItems = (sl: ServiceLinks) => sl.mergeItems;

export const ofLiveSourcesV2 = (sl: ServiceLinks) => sl.liveSourcesV2;
export const ofLiveSourceV2 = (sl: ServiceLinks, liveSourceId: string) => getPath(sl.liveSourceV2, { id: liveSourceId });
export const ofLivePipelines = (sl: ServiceLinks) => sl.livePipelines;

export const ofLiveLocations = (sl: ServiceLinks) => sl.liveLocations;
export const ofLiveLocation = (sl: ServiceLinks, locationId: string) => getPath(sl.liveLocation, { id: locationId });

export const ofRouters = (sl: ServiceLinks) => sl.routers;
export const ofRouter = (sl: ServiceLinks, routerId: string) => getPath(sl.router, { id: routerId });
export const ofRouterSources = (sl: ServiceLinks, routerId: string) => getPath(sl.routerSources, { id: routerId });
export const ofRouterCrosspoints = (sl: ServiceLinks, routerId: string) => getPath(sl.routerCrosspoints, { id: routerId });
export const ofRouterMessages = (sl: ServiceLinks, routerId: string) => getPath(sl.routerMessages, { id: routerId });
export const ofAllRouterSources = (sl: ServiceLinks) => sl.allRouterSources;
export const ofAllRouterControllerStatuses = (sl: ServiceLinks) => sl.allRouterControllerStatuses;

export const ofNdiRouter = (sl: ServiceLinks, liveSourceId: string, pipelineId: string) => getPath(sl.ndiRouter, { liveSourceId, pipelineId });
export const ofMediaPipelineRouter = (sl: ServiceLinks, liveSourceId: string, pipelineId: string) => getPath(sl.mediaPipelineRouter, { liveSourceId, pipelineId: encodeURIComponent(pipelineId) });
export const ofKeldaMediaPipelineStatus = (sl: ServiceLinks, liveSourceId: string, pipelineId: string) => getPath(sl.keldaMediaPipelineStatus, { liveSourceId, pipelineId: encodeURIComponent(pipelineId) });
export const ofNdiSources = (sl: ServiceLinks) => sl.ndiSources;
export const ofDynamicSourceList = (sl: ServiceLinks, sourceListKey: SourceListKey) => getPath(sl.dynamicSourceList, { sourceListKey });

export const ofDeduplication = (sl: ServiceLinks) => sl.deduplication;

export const ofItemEditStatus = (sl: ServiceLinks, itemId: string) => getPath(sl.itemEditStatus, { id: itemId });
export const ofKeldaS3ServerKeys = (sl: ServiceLinks, keldaId: string) => getPath(sl.keldaS3ServerKeys, { keldaId });

export const ofGetIPAddress = (sl: ServiceLinks) => sl.getIPAddress;

export const ofExternalShare = (sl: ServiceLinks, shareSecret: string) => getPath(sl.externalShare, { id: shareSecret });
export const ofExternalShares = (sl: ServiceLinks) => sl.externalShares;
export const ofShareInvitations = (sl: ServiceLinks, shareSecret: string) => getPath(sl.shareInvitations, { id: shareSecret });


export const ofCustomAction = (sl: ServiceLinks, customActionId: string) => getPath(sl.customAction, { id: customActionId });
export const ofCustomActions = (sl: ServiceLinks) => sl.customActions;

export const ofUserTask = (assignee: string, taskId: string, sl: ServiceLinks) => getPath(sl.userTask, { assignee, taskId });
export const ofCustomIcon = (sl: ServiceLinks, customIconId: string) => getPath(sl.customIcon, { id: encodeURIComponent(customIconId) });
export const ofCustomIcons = (sl: ServiceLinks) => sl.customIcons;

export const ofTenantLogo = (sl: ServiceLinks) => sl.tenantLogo;

export const ofUserTasks = (assignee: string, sl: ServiceLinks) => getPath(sl.userTasks, { assignee });

export const ofWebhook = (sl: ServiceLinks, webhookId: string) => getPath(sl.webhook, { id: webhookId });
export const ofWebhooks = (sl: ServiceLinks) => sl.webhooks;

export const ofBatchSignUploadURLs = (sl: ServiceLinks) => sl.batchJobUploadUrls;

export const ofBatchConcludeJobUploadURLs = (sl: ServiceLinks) => sl.batchConcludeJobUpload;

export const ofKeldaIngestFile = (sl: ServiceLinks, keldaId: string) => getPath(sl.keldaIngestFile, {keldaId});

export const ofShareToken = (sl: ServiceLinks) => sl.shareToken;
export const ofShareData = (sl: ServiceLinks) => sl.shareData;
export const ofShareContent = (sl: ServiceLinks) => sl.shareContent;
export const ofShareComments = (sl: ServiceLinks) => sl.shareComments;
export const ofShareCommentReactions = (sl: ServiceLinks, commentId: string) => getPath(sl.shareCommentReactions, { commentId });
export const ofShareRequestOTP = (sl: ServiceLinks) => sl.externalRequestOTP;
export const ofShareItemUpload = (sl: ServiceLinks, itemId: string) => getPath(sl.shareItemUpload, { itemId: itemId });
export const ofShareIngestFileFromExternalS3 = (sl: ServiceLinks, itemId: string) => getPath(sl.shareIngestFileFromExternalS3, { itemId: itemId });
export const ofShareUploadProgress = (sl: ServiceLinks) => sl.shareUploadProgress;
export const ofShareFolderUpload = (sl: ServiceLinks, folderId: string) => getPath(sl.shareFolderUpload, { folderId });
export const ofShareDownload = (sl: ServiceLinks, itemId: string) => getPath(sl.shareDownload, { itemId: itemId });
export const ofShareMdfs = (sl: ServiceLinks, formId: string) => getPath(sl.shareMdfs, { id: formId });
export const ofShareOptionList = (sl: ServiceLinks, optionListId: string) => getPath(sl.shareOptionList, { id: optionListId });
export const ofShareMetadata = (sl: ServiceLinks, itemId: string) => getPath(sl.shareItemMetadata, { itemId });
export const ofShareTranscript = (sl: ServiceLinks, itemId: string) => getPath(sl.shareItemTranscript, { itemId });
export const ofShareSegments = (sl: ServiceLinks, itemId: string, streamIds: string, firstSegmentIndex: string) => getPath(sl.shareMediaSegments, { itemId, streamIds, signedForMethod: 'get', firstSegmentIndex });
export const ofShareRecordLog = (sl: ServiceLinks) => sl.shareRecordLog;

export const ofStorages = (sl: ServiceLinks) => sl.storages;
export const ofStorageTransfers = (sl: ServiceLinks) => sl.storageTransfers;

export const ofItemUpload = (sl: { itemUpload: string }, itemId: string) => getPath(sl.itemUpload, { itemId });
export const ofPendingOperationsSummary = (sl: ServiceLinks) => sl.pendingOperationsSummary;

export const ofHousekeepingRun = (sl: ServiceLinks) => sl.housekeepingRun;
export const ofHousekeepingState = (sl: ServiceLinks, taskId: string) => getPath(sl.housekeepingState, { taskId });

export const ofRenderSequenceRequests = (sl: ServiceLinks) => sl.renderSequenceRequests;
export const ofItemRender = (sl: ServiceLinks, itemId: string) => getPath(sl.itemRender, { itemId });

export const ofMxcoderEvents = (sl: ServiceLinks) => sl.mxcoderEvents;
export const ofMxcoderItem = (sl: ServiceLinks, itemId: string) => getPath(sl.mxcoderItem, { itemId });
export const ofMxcoderTargetItem = (sl: ServiceLinks) => sl.mxcoderTargetItem;
export const ofMxcoderMediaSegments = (sl: ServiceLinks, itemId: string, streamIds: StreamRoleId[], signedForMethod: 'get' | 'put', firstSegmentIndex: number) =>
  getPath(sl.mxcoderMediaSegments, { itemId, streamIds: streamIds.join(','), signedForMethod, firstSegmentIndex: `${firstSegmentIndex}` });
export const ofMxcoderSceneDetectionParams = (sl: ServiceLinks) => sl.mxcoderSceneDetectionParams;
export const ofMxcoderProxygenParams = (sl: ServiceLinks) => sl.mxcoderProxygenParams;
export const ofMxcoderRenderSequenceParams = (sl: ServiceLinks) => sl.mxcoderRenderSequenceParams;
export const ofMxCoderUpdateProgress = (sl: ServiceLinks, itemId: string) => getPath(sl.mxcoderUpdateProgress, { itemId });
export const ofMxcoderItemAttachment = (sl: ServiceLinks, itemId: string) => getPath(sl.mxcoderItemAttachment, { itemId });
export const ofMxcoderItemAttachments = (sl: ServiceLinks, itemId: string) => getPath(sl.mxcoderItemAttachments, { itemId });
export const ofMxcoderRenderChunkConcatenationParams = (sl: ServiceLinks) => sl.mxcoderRenderChunkConcatenationParams;
export const ofMxcoderLuts = (sl: ServiceLinks) => sl.mxcoderLuts;
export const ofMxcoderAudioChannels = (sl: ServiceLinks, itemId: string) => getPath(sl.mxcoderAudioChannels, { itemId });
export const ofMxcoderPrepareTranscriptionInputParams = (sl: ServiceLinks) => sl.mxcoderPrepareTranscriptionInputParams;
export const ofReplication = (sl: ServiceLinks) => sl.replication;

export const ofPlayableItem = (sl: ServiceLinks, itemId: string) => getPath(sl.playableItem, { itemId });
export const ofSharePlayableItem = (sl: ServiceLinks) => sl.sharePlayableItem;
export const ofSharePeaks = (sl: ServiceLinks, itemId: string, audioTrackIndex: number) => getPath(sl.sharePeaks, { itemId, audioTrackId: `${audioTrackIndex}` });
export const ofSharePeaksDelta = (sl: ServiceLinks, itemId: string, peakOffset: number) => getPath(sl.sharePeaksDelta, { itemId, peakOffset: `${peakOffset}` });
export const ofShareItemPolling = (sl: ServiceLinks) => sl.shareItemPolling;

export const ofTamsToken = (sl: ServiceLinks, id: string) => getPath(sl.tamsToken, { id });

/**
 * audioChannelIndex is a value from 0 to N-1 where N is the total number of solo channels
 * (for instance N=16 if the media has 8 stereo audio streams)
 */
export const ofPeaks = (sl: ServiceLinks, itemId: string, audioChannelIndex: number) => getPath(sl.peaks, { itemId, audioTrackId: `${audioChannelIndex}` });
export const ofPeaksDelta = (sl: ServiceLinks, itemId: string, peakOffset: number) => getPath(sl.peaksDelta, { itemId, peakOffset: `${peakOffset}` });
export const ofItemPolling = (sl: ServiceLinks) => sl.itemPolling;

export const ofUrlArray = (sl: ServiceLinks) => sl.urlArray;
export const ofSharableUrlArray = (sl: ServiceLinks) => sl.shareUrlArray;

export const ofWebsocketInfo = (sl: ServiceLinks, keldaId: string, keldaInstanceId: string) => getPath(sl.websocketInfo, { keldaId, keldaInstanceId });

export const ofPlugins = (sl: ServiceLinks) => sl.plugins;
export const ofPlugin = (sl: ServiceLinks, pluginId: string) => getPath(sl.plugin, { id: pluginId });

export const ofOfflineStoragePlugin = (sl: ServiceLinks, pluginId: string) => getPath(sl.offlineStoragePlugin, { pluginId });
export const ofOfflineStoragePlugins = (sl: ServiceLinks) => sl.offlineStoragePlugins;

export const ofSsoConfig = (sl: ServiceLinks) => sl.ssoConfig;

export const ofItemExternalHighresInfoAppendRetrievedSegment = (sl: ServiceLinks, itemId: string) => getPath(sl.itemExternalHighresInfoAppendRetrievedSegment, { itemId });

export const ofItemExternalHighresInfo = (sl: ServiceLinks, itemId: string) => getPath(sl.itemExternalHighresInfo, { itemId });

export const ofOfflineStorageJob = (sl: ServiceLinks, jobId: string) => getPath(sl.offlineStorageJob, { jobId });
export const ofOfflineStorageJobs = (sl: ServiceLinks, pluginId: string) => getPath(sl.offlineStorageJobs, { pluginId });
export const ofItemOfflineStorageInfo = (sl: ServiceLinks, itemId: string) => getPath(sl.itemOfflineStorageInfo, { itemId });

export const ofVttIds = (sl: ServiceLinks, itemId: string) => getPath(sl.vttIds, { itemId });
export const ofVttCues = (sl: ServiceLinks, itemId: string, vttId: VttId) => getPath(sl.vttCues, {
  itemId,
  vttType: vttId.type,
  vttId: encodeURIComponent(vttId.type === 'growingVtt' ? vttId.closedCaptionsKey : vttId.vttFilename),
});

export const ofMetagfxTemplates = (sl: ServiceLinks) => sl.metagfxTemplates;
export const ofMetagfxThumbnail = (sl: ServiceLinks, templateId: string) => getPath(sl.metagfxTemplateThumbnail, { templateId });
export const ofMetagfxRenderingInfo = (sl: ServiceLinks) => sl.metagfxRenderingInfo;

export const ofLuts = (sl: ServiceLinks) => sl.luts;
export const ofCompleteLut = (sl: ServiceLinks, id: string) => getPath(sl.completeLut, { id });
export const ofDeleteLut = (sl: ServiceLinks, id: string) => getPath(sl.deleteLut, { id });
